/**
 * Job Data Validation Utilities
 * 
 * Ensures job data integrity and consistency across the application.
 * Validates job structures and provides data normalization.
 */

/**
 * Validate job data structure
 * @param {Object} jobData - Job data to validate
 * @param {Object} options - Validation options
 * @returns {Object} Validation result with isValid and errors
 */
export const validateJobData = (jobData, options = {}) => {
  const errors = [];
  const { 
    isSimilarityResult = false,
    requireUserId = true 
  } = options;
  
  // Required fields - adjust based on context
  const requiredFields = [
    'jobId',
    'orderId', // CRITICAL: Must always be present
    'casefileId',
    'evidenceId',
    'provider'
  ];
  
  // Add conditional required fields
  if (requireUserId && !isSimilarityResult) {
    requiredFields.push('userId');
  }
  
  if (!isSimilarityResult) {
    requiredFields.push('status');
  }
  
  requiredFields.forEach(field => {
    if (!jobData[field]) {
      errors.push(`Missing required field: ${field}`);
    }
  });
  
  // Validate orderId specifically
  if (jobData.orderId && typeof jobData.orderId !== 'string') {
    errors.push('orderId must be a string');
  }
  
  if (jobData.orderId && jobData.orderId.length < 10) {
    errors.push('orderId appears to be invalid (too short)');
  }
  
  // Validate status
  const validStatuses = ['queued', 'processing', 'completed', 'failed', 'cancelled'];
  if (jobData.status && !validStatuses.includes(jobData.status)) {
    errors.push(`Invalid status: ${jobData.status}. Must be one of: ${validStatuses.join(', ')}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Normalize job data to ensure consistency
 * @param {Object} jobData - Raw job data
 * @returns {Object} Normalized job data
 */
export const normalizeJobData = (jobData) => {
  const normalized = { ...jobData };
  
  // Ensure jobId is set
  if (!normalized.jobId && normalized.id) {
    normalized.jobId = normalized.id;
  }
  
  // Ensure orderId is always present
  if (!normalized.orderId) {
    console.error('🚨 CRITICAL: Job missing orderId:', normalized);
    throw new Error('Job data must include orderId');
  }
  
  // Normalize timestamps
  if (normalized.createdAt && typeof normalized.createdAt.toMillis === 'function') {
    normalized.createdAtMs = normalized.createdAt.toMillis();
  }
  
  // Ensure provider is lowercase
  if (normalized.provider) {
    normalized.provider = normalized.provider.toLowerCase();
  }
  
  // Ensure selectedServices is an array
  if (normalized.selectedServices && !Array.isArray(normalized.selectedServices)) {
    normalized.selectedServices = [normalized.selectedServices];
  }
  
  return normalized;
};

/**
 * Validate and normalize job data in one step
 * @param {Object} jobData - Raw job data
 * @param {Object} options - Validation options
 * @returns {Object} Validated and normalized job data
 * @throws {Error} If validation fails
 */
export const validateAndNormalizeJob = (jobData, options = {}) => {
  // First normalize
  const normalized = normalizeJobData(jobData);
  
  // Then validate with options
  const validation = validateJobData(normalized, options);
  
  if (!validation.isValid) {
    const errorMessage = `Job validation failed: ${validation.errors.join(', ')}`;
    console.error('🚨 Job validation error:', errorMessage, normalized);
    throw new Error(errorMessage);
  }
  
  return normalized;
};

/**
 * Ensure job has orderId by looking it up if missing
 * @param {Object} jobData - Job data that might be missing orderId
 * @returns {Promise<Object>} Job data with guaranteed orderId
 */
export const ensureJobHasOrderId = async (jobData) => {
  // If orderId is already present and valid, return as-is
  if (jobData.orderId && typeof jobData.orderId === 'string' && jobData.orderId.length > 10) {
    return jobData;
  }
  
  console.warn('🔧 Job missing orderId, attempting to look up:', jobData.jobId || jobData.id);
  
  // Try to look up orderId from the jobs collection
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('../firebase');
    
    const jobId = jobData.jobId || jobData.id;
    if (!jobId) {
      throw new Error('Cannot look up orderId: no jobId available');
    }
    
    const jobDoc = await getDoc(doc(db, 'jobs', jobId));
    if (jobDoc.exists()) {
      const fullJobData = jobDoc.data();
      if (fullJobData.orderId) {
        console.log('✅ Found orderId for job:', jobId, '→', fullJobData.orderId);
        return { ...jobData, orderId: fullJobData.orderId };
      }
    }
    
    throw new Error(`Job ${jobId} exists but has no orderId`);
  } catch (error) {
    console.error('❌ Failed to look up orderId for job:', jobData.jobId || jobData.id, error);
    throw new Error(`Cannot ensure orderId for job: ${error.message}`);
  }
};

/**
 * Batch validate multiple jobs
 * @param {Array} jobs - Array of job data objects
 * @returns {Object} Validation summary with valid jobs and errors
 */
export const batchValidateJobs = (jobs) => {
  const validJobs = [];
  const invalidJobs = [];
  const errors = [];
  
  jobs.forEach((job, index) => {
    try {
      const normalized = validateAndNormalizeJob(job);
      validJobs.push(normalized);
    } catch (error) {
      invalidJobs.push({ job, index, error: error.message });
      errors.push(`Job ${index}: ${error.message}`);
    }
  });
  
  return {
    validJobs,
    invalidJobs,
    errors,
    summary: {
      total: jobs.length,
      valid: validJobs.length,
      invalid: invalidJobs.length
    }
  };
};
